import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiV1 } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Mail } from 'lucide-react';
import { HireIqLogo } from '@/components/HireIqLogo';
import { TurnstileWidget } from '@/components/TurnstileWidget';
import { LOGIN } from '@/constants/testIds/auth';

/**
 * Local-auth forgot password. Always shows a generic success message.
 */
export default function ForgotPassword() {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiV1.get('/auth/config');
        if (!cancelled) setCfg(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onCaptcha = useCallback((token) => setCaptchaToken(token), []);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!cfg?.supports_forgot_password) {
      setErr('Password reset is not available on this deployment.');
      return;
    }
    if (cfg.captcha_site_key && !captchaToken) {
      setErr('Please complete the captcha.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiV1.post('/auth/password/forgot', {
        email: email.trim(),
        captcha_token: captchaToken || undefined,
      });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm" data-testid="forgot-password-card">
        <HireIqLogo size="xl" className="mx-auto max-w-[200px] !object-center" />
        <p className="mt-4 text-lg font-semibold tracking-tight">Forgot password</p>
        <p className="text-xs text-muted-foreground">
          Enter your work email. If an account exists, we will send a reset link.
        </p>

        {done ? (
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="forgot-password-success">
            If an account exists for that email, you will receive password reset instructions shortly.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-password-form">
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="forgot-email">Email</label>
              <Input
                id="forgot-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-password-email-input"
              />
            </div>
            {cfg?.captcha_site_key ? (
              <TurnstileWidget siteKey={cfg.captcha_site_key} onToken={onCaptcha} />
            ) : null}
            <Button type="submit" disabled={busy} className="w-full gap-2" size="lg" data-testid="forgot-password-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {busy ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}

        {err && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="forgot-password-error">
            {typeof err === 'string' ? err : JSON.stringify(err)}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/login" className="text-primary underline-offset-2 hover:underline" data-testid={LOGIN.forgotPasswordLink ? 'forgot-back-to-login' : 'forgot-back-to-login'}>
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
